import { BillOrInvoiceRailz, withStyles } from 'vopay';
import { DropdownMenu } from 'radix-ui';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import useFileDownload from '#shared/utils/useFileDownload';
import { useToast } from '#shared/providers/Toast';
import { getLastPaymentDate, getPaymentMethodFromPayments } from '../AccountingSystemClosed/AccountingSystemClosed';
import type { DateFilterType } from '../AccountingSystemSearchForm';
import styles from './AccountingSystemExport.module.scss';

import { UtilButton } from '#shared/components/UI';

const cn = withStyles(styles);

const EXPORT_FORMATS = ['Excel', 'CSV', 'PDF'] as const;

type AccountingSystemExportProps = {
  // The full filtered dataset — not the visible page.
  billsOrInvoices: BillOrInvoiceRailz[];
  type: 'Payable' | 'Receivable';
  activeTab: 'open' | 'closed';
  dateFilterType: DateFilterType;
  serviceName: string;
};

export default function AccountingSystemExport({
  billsOrInvoices,
  type,
  activeTab,
  dateFilterType,
  serviceName,
}: AccountingSystemExportProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const { downloadCsvData, downloadXlsData, downloadPdfTableData } = useFileDownload();
  const toast = useToast();

  const isReceivable = type === 'Receivable';

  // Mirrors the open tab's status column, as plain text.
  const statusOf = (row: BillOrInvoiceRailz) => {
    if (row.status?.toLowerCase() === 'reversed')
      return t('OpenTab.Table.Row.statusReversed', { defaultValue: 'Reversed' });

    if (row.status === 'partiallyPaid') {
      return isReceivable
        ? t('OpenTab.Table.Row.statusPartiallyCollected', { defaultValue: 'Partially Collected' })
        : t('OpenTab.Table.Row.statusPartiallyPaid', { defaultValue: 'Partially Paid' });
    }

    return t('OpenTab.Table.Row.statusOpen', { defaultValue: 'Open' });
  };

  // Mirrors the closed tab's transaction column, as plain text.
  const transactionsOf = (row: BillOrInvoiceRailz) => {
    if (getPaymentMethodFromPayments(row.PaymentDetails) === 'Paid using Credits') return '';

    const transactionIds = row.PaymentDetails.filter(
      (payment) => payment.TransactionID && payment.TransactionID !== '',
    ).map((payment) => payment.TransactionID);

    if (transactionIds.length > 0) return transactionIds.join(', ');

    if (row.PaymentDetails.some((payment) => payment.PaymentRail))
      return t('CloseTab.paymentProcessing', { defaultValue: 'Payment processing' });

    return t('CloseTab.paidUsing', { defaultValue: 'Paid using {{serviceName}}', serviceName });
  };

  const methodOf = (row: BillOrInvoiceRailz) => {
    const method = getPaymentMethodFromPayments(row.PaymentDetails);
    if (method === 'Paid using Credits') return t('CloseTab.paidUsingCredits', { defaultValue: 'Paid using Credits' });
    if (method === 'Multiple') return t('CloseTab.multiple', { defaultValue: 'Multiple' });

    return method;
  };

  const asDate = (value: string) => (value ? format(new Date(value.replace('Z', '')), 'yyyy-MM-dd') : '');
  const amountOf = (row: BillOrInvoiceRailz) => `${row.currency || ''} ${row.totalAmount}`;

  // Columns and order mirror the rendered table of the active tab, including
  // the closed tab's date anchoring.
  const buildTableData = () => {
    const nameHeader = isReceivable
      ? t('CloseTab.ColumnHeader.customer', { defaultValue: 'Customer' })
      : t('CloseTab.ColumnHeader.supplier', { defaultValue: 'Supplier' });

    const numberHeader = isReceivable
      ? t('CloseTab.ColumnHeader.invoiceNumber', { defaultValue: 'Invoice No.' })
      : t('CloseTab.ColumnHeader.billNumber', { defaultValue: 'Bill No.' });

    const nameOf = (row: BillOrInvoiceRailz) =>
      row.Customer?.Name || row.customerRef?.name || row.vendorRef?.name || '';

    const numberOf = (row: BillOrInvoiceRailz) => row.invoiceNumber || row.vendorInvoiceNumber || '';

    if (activeTab === 'open') {
      return {
        headers: [
          isReceivable
            ? t('OpenTab.Table.dateReceivable', { defaultValue: 'Invoice Date' })
            : t('OpenTab.Table.date', { defaultValue: 'Bill Date' }),
          nameHeader,
          numberHeader,
          t('OpenTab.Table.amount', { defaultValue: 'Amount' }),
          t('OpenTab.Table.Row.remainingLabel', { defaultValue: 'Remaining' }),
          t('OpenTab.Table.dueDate', { defaultValue: 'Due Date' }),
          t('OpenTab.Table.status', { defaultValue: 'Status' }),
        ],
        rows: billsOrInvoices.map((row) => [
          asDate(row.postedDate),
          nameOf(row),
          numberOf(row),
          amountOf(row),
          `${row.currency || ''} ${row.amountDue}`,
          asDate(row.dueDate),
          statusOf(row),
        ]),
      };
    }

    const settlementHeader = isReceivable
      ? t('CloseTab.ColumnHeader.receivedDate', { defaultValue: 'Received Date' })
      : t('CloseTab.ColumnHeader.paymentDate', { defaultValue: 'Payment Date' });

    const settlementOf = (row: BillOrInvoiceRailz) => getLastPaymentDate(row.PaymentDetails, row.postedDate);
    const documentDateOf = (row: BillOrInvoiceRailz) => asDate(row.postedDate);

    const documentDateHeader = isReceivable
      ? t('CloseTab.ColumnHeader.invoiceDate', { defaultValue: 'Invoice Date' })
      : t('CloseTab.ColumnHeader.billDate', { defaultValue: 'Bill Date' });

    const isDocumentDateAnchored = dateFilterType === 'documentDate';

    return {
      headers: [
        isDocumentDateAnchored ? documentDateHeader : settlementHeader,
        nameHeader,
        numberHeader,
        t('CloseTab.ColumnHeader.amount', { defaultValue: 'Amount' }),
        isDocumentDateAnchored ? settlementHeader : documentDateHeader,
        t('CloseTab.ColumnHeader.paymentMethod', { defaultValue: 'Payment Method' }),
        t('CloseTab.ColumnHeader.vopayTransactionId', { defaultValue: 'VoPay Transaction ID' }),
      ],
      rows: billsOrInvoices.map((row) => [
        isDocumentDateAnchored ? documentDateOf(row) : settlementOf(row),
        nameOf(row),
        numberOf(row),
        amountOf(row),
        isDocumentDateAnchored ? settlementOf(row) : documentDateOf(row),
        methodOf(row),
        transactionsOf(row),
      ]),
    };
  };

  const exportAs = (exportFormat: (typeof EXPORT_FORMATS)[number]) => {
    try {
      const tab = activeTab === 'open' ? 'outstanding' : 'closed';
      const fileName = `${type.toLowerCase()}s-${tab}-${format(new Date(), 'yyyy-MM-dd')}`;
      const tableData = buildTableData();

      if (exportFormat === 'CSV') downloadCsvData(tableData, fileName);
      if (exportFormat === 'Excel') downloadXlsData(tableData, fileName);
      if (exportFormat === 'PDF') downloadPdfTableData(tableData, fileName);

      toast.success(
        t('Export.success', {
          defaultValue: 'Export complete — {{numItems}} item(s) downloaded.',
          numItems: tableData.rows.length,
        }),
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(t('Export.error', { defaultValue: 'Could not export the list: {{errorMessage}}', errorMessage }));
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <UtilButton
          data-testid="export-accounting"
          iconLeftProps={{ icon: 'download' }}
          iconRight="keyboard_arrow_down"
        >
          {tGlobal('Buttons.export', { defaultValue: 'Export' })}
        </UtilButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={cn('contentContainer')}>
          {EXPORT_FORMATS.map((exportFormat) => (
            <DropdownMenu.Item key={exportFormat} className={cn('item')} onSelect={() => exportAs(exportFormat)}>
              {exportFormat}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
